import {useEffect, useState} from 'react'
import {serverLocalName} from 'GLOBAL/envVariables'

/**
 * 사업소별 통신주기 필터 설정.
 *
 * 필터를 어느 화면에 노출할지, 선택지로 어떤 주기를 줄지가 사업소마다 다르고
 * 운영 중에도 바뀐다(괴산 6시간 → 4시간 같은 요청이 반복된다). 코드에 두면 그때마다
 * 재빌드·재배포를 해야 하므로, 설치 판정 기준(signalConfig)과 같은 방식으로
 * 서버의 JSON 파일만 교체하면 바뀌도록 분리한다.
 *
 * 파일이 없거나 형식이 어긋나면 기본값(기존 하드코딩 값)으로 계속 동작한다 —
 * 필터가 엉뚱하게 사라지는 것보다 이전 동작을 유지하는 쪽이 안전하다.
 */

/** 통신주기 필터를 노출할 수 있는 화면 */
export type ServerCyclePage = 'info' | 'status' | 'hdm'

/** 화면 순서와 이름. 편집 UI 와 검증이 이 배열 하나를 함께 본다. */
export const SERVER_CYCLE_PAGES: {id: ServerCyclePage; label: string}[] = [
  {id: 'info', label: '수용가 정보'},
  {id: 'status', label: '수용가 현황'},
  {id: 'hdm', label: '검침 정보'},
]

/** 사업소 전체를 뜻하는 officeId. 괴산처럼 서버 하나가 사업소 하나인 곳에 쓴다. */
export const ALL_OFFICES = '*'

export interface ServerCycleOffice {
  /**
   * 적용할 사업소 코드. `*` 면 이 서버의 모든 사업소.
   *
   * 코드 체계상 하위 사업소는 상위 코드로 시작하므로(2자리 본부 → 4자리 사업소 →
   * 6자리 상수/지하수), 본부 코드를 넣으면 그 아래 전부에 적용된다. 한 사업소만
   * 다르게 두고 싶으면 그 사업소 코드로 줄을 따로 만들면 된다.
   */
  officeId: string
  /** 화면에 표시할 이름. 판정에는 쓰지 않는 메모다. */
  label: string
  /**
   * 필터를 노출할 화면. 곧 화면별 on/off 이며, 비어 있으면 어디에도 노출하지 않는다.
   */
  pages: ServerCyclePage[]
  /** 선택지. `{주기(시간): 라벨}` 형태다. */
  options: Record<string, string>
}

export interface ServerCycleConfig {
  offices: ServerCycleOffice[]
}

/** 설정 파일 경로. docker-compose 볼륨 마운트 대상이라 임의로 바꾸지 않는다. */
export const SERVER_CYCLE_CONFIG_PATH = '/config/server-cycle-config.json'

/** 필터를 쓰지 않는 사업소의 설정 */
const noServerCycle: ServerCycleOffice = {
  officeId: ALL_OFFICES,
  label: '',
  pages: [],
  options: {},
}

/**
 * 기본값. 설정 파일이 없을 때 쓰이며, 기존에 코드로 박혀 있던 값 그대로다.
 *
 * 괴산은 자체 서버라 이 서버에 붙는 사업소가 모두 괴산이므로 `*` 로 둔다.
 * 진주는 여러 사업소가 붙는 서버라 사업소 ID 로 좁힌다.
 */
export const defaultServerCycleConfig = (serverName: string): ServerCycleConfig => {
  switch (serverName) {
    case 'prod':
    case 'dev':
      return {
        offices: [
          {
            officeId: '84',
            label: '진주',
            pages: ['info', 'status', 'hdm'],
            options: {1: '1시간', 6: '6시간'},
          },
        ],
      }
    case 'goesan':
      return {
        offices: [
          {
            officeId: ALL_OFFICES,
            label: '괴산',
            pages: ['info'],
            options: {4: '4시간', 23: '23시간'},
          },
        ],
      }
    default:
      return {offices: []}
  }
}

/** 현재 적용 중인 설정. 필터 판정은 렌더 중 동기로 읽으므로 모듈 레벨에 캐시한다. */
let currentConfig: ServerCycleConfig = defaultServerCycleConfig(serverLocalName)

export interface ServerCycleConfigStatus {
  /** default = 기본값으로 동작 중, remote = 설정 파일 적용됨 */
  source: 'default' | 'remote'
  /** 마지막으로 설정 파일을 적용한 시각 */
  loadedAt: Date | null
  /** 마지막 실패 사유. 서버에 접속하지 않고 원인을 확인해야 하므로 남겨둔다. */
  error: string | null
}

let currentStatus: ServerCycleConfigStatus = {source: 'default', loadedAt: null, error: null}

export const getServerCycleConfig = (): ServerCycleConfig => currentConfig
export const getServerCycleConfigStatus = (): ServerCycleConfigStatus => currentStatus

/**
 * 이 사업소에 적용할 설정을 고른다.
 *
 * 하위 사업소 코드는 상위 코드로 시작하므로 앞부분이 일치하는 줄을 찾되,
 * 여럿이면 가장 긴(= 가장 구체적인) 줄이 이긴다. 본부 단위로 깔아두고 특정
 * 사업소만 예외를 두는 식이 자연스럽게 된다. 맞는 줄이 없으면 `*` 행을 쓴다.
 *
 * 어디에도 걸리지 않으면 필터를 노출하지 않는다 — 설정 파일이 적용 중이라면
 * 그 파일이 전부이므로, 운영자가 줄을 지웠다면 "필터를 끄겠다"는 뜻이다.
 */
export const matchServerCycleOffice = (officeId: any): ServerCycleOffice => {
  const id = String(officeId ?? '')
  const {offices} = currentConfig

  const matched = offices
    .filter((office) => office.officeId !== ALL_OFFICES && id.startsWith(office.officeId))
    .sort((a, b) => b.officeId.length - a.officeId.length)[0]

  return matched ?? offices.find((office) => office.officeId === ALL_OFFICES) ?? noServerCycle
}

const isPage = (value: unknown): value is ServerCyclePage =>
  SERVER_CYCLE_PAGES.some((page) => page.id === value)

/**
 * 선택지 검증. 키는 시간 단위 주기라 양의 정수여야 하고, 라벨은 문자열이어야 한다.
 * 키가 숫자가 아니면 조회 조건으로 서버에 그대로 나가 엉뚱한 결과를 부른다.
 */
const parseOptions = (raw: any, rowLabel: string): Record<string, string> | string => {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
    return `${rowLabel} options 는 객체여야 합니다.`
  }
  const entries = Object.entries(raw)
  if (entries.length === 0) return `${rowLabel} 선택지가 최소 1개는 있어야 합니다.`

  const parsed: Record<string, string> = {}
  for (const [cycle, label] of entries) {
    const hours = Number(cycle)
    if (!Number.isInteger(hours) || hours <= 0) {
      return `${rowLabel} 주기(${cycle})는 1 이상의 정수여야 합니다.`
    }
    if (typeof label !== 'string' || label.trim() === '') {
      return `${rowLabel} ${cycle}시간의 라벨이 비어 있습니다.`
    }
    parsed[String(hours)] = label
  }
  return parsed
}

/**
 * 외부 JSON 을 검증해 ServerCycleConfig 로 변환한다.
 * 한 군데라도 어긋나면 통째로 거부한다 — 사업소 절반만 반영되면 원인을 찾기 더 어렵다.
 *
 * @returns 유효하면 설정, 아니면 사유 문자열
 */
export const parseServerCycleConfig = (raw: any): ServerCycleConfig | string => {
  if (!raw || typeof raw !== 'object') return '설정 형식이 올바르지 않습니다.'
  if (!Array.isArray(raw.offices)) return 'offices 는 배열이어야 합니다.'

  const offices: ServerCycleOffice[] = []
  const seen = new Set<string>()

  for (let index = 0; index < raw.offices.length; index++) {
    const row = raw.offices[index]
    const rowLabel = `${index + 1}번째 사업소의`
    if (!row || typeof row !== 'object') return `${rowLabel} 형식이 올바르지 않습니다.`

    const {officeId, label, pages, options} = row
    if (typeof officeId !== 'string' || officeId.trim() === '') {
      return `${rowLabel} 사업소 ID 가 비어 있습니다.`
    }
    const id = officeId.trim()
    /* 같은 사업소가 두 번 나오면 뒤 행이 조용히 무시된다. 저장 전에 잡는다. */
    if (seen.has(id)) return `사업소 ID ${id} 가 중복됩니다.`
    seen.add(id)

    if (typeof label !== 'undefined' && typeof label !== 'string') {
      return `${rowLabel} 이름은 문자열이어야 합니다.`
    }
    if (!Array.isArray(pages) || !pages.every(isPage)) {
      return `${rowLabel} 노출 화면 값을 알 수 없습니다.`
    }

    const parsedOptions = parseOptions(options, rowLabel)
    if (typeof parsedOptions === 'string') return parsedOptions

    offices.push({officeId: id, label: label ?? '', pages, options: parsedOptions})
  }

  return {offices}
}

/** 편집 UI 에서 저장할 때 쓰는 직렬화. */
export const stringifyServerCycleConfig = (config: ServerCycleConfig): string =>
  JSON.stringify(
    {
      offices: config.offices.map((office) => ({
        officeId: office.officeId,
        label: office.label,
        pages: office.pages,
        options: office.options,
      })),
    },
    null,
    2
  )

/**
 * 같은 화면 안에서 여러 곳이 동시에 설정을 필요로 한다(레이아웃과 페이지가 각각 읽는다).
 * 그때마다 요청을 보내지 않도록 진행 중인 요청 하나를 함께 기다린다.
 */
let inFlight: Promise<ServerCycleConfig> | null = null

/**
 * 설정 파일을 읽어 적용한다.
 * 어떤 실패에서도 예외를 던지지 않고, 직전 설정(없으면 기본값)을 그대로 유지한다.
 */
export const loadServerCycleConfig = (timeoutMs: number = 3000): Promise<ServerCycleConfig> => {
  if (inFlight) return inFlight

  inFlight = (async () => {
    /* 통신이 느린 환경에서 응답을 기다리며 화면이 멈추지 않도록 제한을 둔다 */
    const controller = new AbortController()
    const timer = setTimeout(() => controller.abort(), timeoutMs)

    try {
      const res = await fetch(`${SERVER_CYCLE_CONFIG_PATH}?ts=${Date.now()}`, {
        cache: 'no-store',
        signal: controller.signal,
      })
      const contentType = res.headers.get('content-type') ?? ''

      /* 파일이 없으면 nginx SPA fallback 이 index.html 을 200 으로 돌려주므로
         상태 코드만으로는 판별할 수 없다. */
      if (!res.ok || !contentType.includes('json')) {
        currentStatus = {...currentStatus, error: '설정 파일을 찾을 수 없습니다.'}
        return currentConfig
      }

      const parsed = parseServerCycleConfig(await res.json())
      if (typeof parsed === 'string') {
        currentStatus = {...currentStatus, error: parsed}
        return currentConfig
      }

      currentConfig = parsed
      currentStatus = {source: 'remote', loadedAt: new Date(), error: null}
    } catch {
      currentStatus = {...currentStatus, error: '설정 파일을 불러오지 못했습니다.'}
    } finally {
      clearTimeout(timer)
      inFlight = null
    }
    return currentConfig
  })()

  return inFlight
}

/**
 * 화면에서 통신주기 필터를 쓰기 위한 훅.
 *
 * 설정을 읽어오는 동안에는 기본값이 나가고, 다 읽으면 다시 그린다.
 * 화면에 들어올 때마다 확인하므로 설정을 바꾼 뒤 새로고침 없이도 반영된다.
 */
export const useServerCycleSetting = (officeId: any): ServerCycleOffice => {
  const [setting, setSetting] = useState<ServerCycleOffice>(() => matchServerCycleOffice(officeId))

  useEffect(() => {
    let alive = true
    loadServerCycleConfig().then(() => {
      if (alive) setSetting(matchServerCycleOffice(officeId))
    })
    return () => {
      alive = false
    }
  }, [officeId])

  return setting
}
